"use client";

import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "@/lib/utm/hooks";
import PressReleaseCard from "@/app/(main)/components/PressReleaseCard";
import type { PressRelease } from "@/lib/pressreleases";
import { fetchPressReleasesClient } from "@/lib/pressreleases";
import type { SystemCountry } from "@/lib/navigation";
import CountryFlag from "@/app/(main)/components/CountryFlag";
import EmptyState from "@/app/(main)/components/EmptyState";

// ─── Props ───────────────────────────────────────────────────────────

interface PressReleasesClientProps {
  initialItems: PressRelease[];
  initialTotalPages: number;
  initialCount: number;
  destinationsMap?: Record<string, string>;
  /** All countries used in the system — backs the destination filter widget. */
  countries?: SystemCountry[];
  /** Destination id resolved from the URL's ?country param (empty = none). */
  initialSelectedDestId?: string;
  pageSize?: number;
  sidebarContent?: ReactNode;
}

// ─── Component ───────────────────────────────────────────────────────

export default function PressReleasesClient({
  initialItems,
  initialTotalPages,
  initialCount,
  destinationsMap = {},
  countries = [],
  initialSelectedDestId = "",
  pageSize = 8,
  sidebarContent,
}: PressReleasesClientProps) {
  const [items, setItems] = useState(initialItems);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [totalCount, setTotalCount] = useState(initialCount);
  const [selectedDest, setSelectedDest] = useState(initialSelectedDestId);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const shouldScroll = useRef(false);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      const top =
        containerRef.current.getBoundingClientRect().top +
        window.scrollY -
        100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, []);

  const loadItems = useCallback(
    async (page: number, destId: string) => {
      setLoading(true);
      try {
        const result = await fetchPressReleasesClient(
          page,
          pageSize,
          destId || undefined,
        );
        setItems(result.items);
        setTotalPages(result.totalPages);
        setTotalCount(result.count);
      } finally {
        setLoading(false);
        if (shouldScroll.current) {
          shouldScroll.current = false;
          requestAnimationFrame(() => scrollToTop());
        }
      }
    },
    [pageSize, scrollToTop],
  );

  useEffect(() => {
    // The SSR render already reflects the initial page/destination, so skip the
    // first client run to avoid a redundant refetch.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadItems(currentPage, selectedDest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedDest]);

  function handlePageChange(page: number) {
    shouldScroll.current = true;
    setCurrentPage(page);
  }

  function handleDestinationChange(destId: string) {
    setCurrentPage(1);
    setSelectedDest(destId);

    // Reflect the selection in the URL's ?country param (the country name, to
    // match how destination pages link here), preserving any other params.
    const params = new URLSearchParams(searchParams.toString());
    const countryName = countries.find((c) => c.id === destId)?.name;
    if (countryName) params.set("country", countryName);
    else params.delete("country");
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Build pagination entries with stable keys
  const paginationEntries: {
    key: string;
    type: "page" | "ellipsis";
    value: number;
  }[] = [];
  if (totalPages > 1) {
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        paginationEntries.push({ key: `p${i}`, type: "page", value: i });
      }
    } else {
      paginationEntries.push({ key: "p1", type: "page", value: 1 });
      if (currentPage > 3) {
        paginationEntries.push({
          key: "el-start",
          type: "ellipsis",
          value: 0,
        });
      }
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        paginationEntries.push({ key: `p${i}`, type: "page", value: i });
      }
      if (currentPage < totalPages - 2) {
        paginationEntries.push({
          key: "el-end",
          type: "ellipsis",
          value: 0,
        });
      }
      paginationEntries.push({
        key: `p${totalPages}`,
        type: "page",
        value: totalPages,
      });
    }
  }

  return (
    <div ref={containerRef}>
      <div className="row">
        {/* ── Main column ── */}
        <div className="col-xxl-8 col-lg-7">
          {/* Pagination header */}
          {totalCount > 0 && (
            <div className="d-flex align-items-center justify-content-between mb-4">
              <p className="blog-text mb-0">
                Showing{" "}
                <strong>
                  {startItem}&ndash;{endItem}
                </strong>{" "}
                of <strong>{totalCount}</strong> press releases
              </p>
              {totalPages > 1 && (
                <div className="d-flex gap-2">
                  <button
                    className="th-btn style4"
                    disabled={currentPage <= 1 || loading}
                    onClick={() => handlePageChange(currentPage - 1)}
                    style={{ padding: "4px 10px" }}
                  >
                    <i className="fa fa-arrow-left"></i> Prev
                  </button>
                  <button
                    className="th-btn style4"
                    disabled={currentPage >= totalPages || loading}
                    onClick={() => handlePageChange(currentPage + 1)}
                    style={{ padding: "4px 10px" }}
                  >
                    Next <i className="fa fa-arrow-right"></i>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Press Release grid */}
          <div
            className="row gy-4 gx-xl-4 g-4"
            style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }}
          >
            {items.map((press) => (
              <div key={press.id} className="col-lg-6">
                <PressReleaseCard
                  press={press}
                  destinationName={
                    press.destinationId
                      ? destinationsMap[press.destinationId]
                      : undefined
                  }
                />
              </div>
            ))}

            {items.length === 0 && !loading && (
              <div className="col-12">
                {selectedDest ? (
                  <EmptyState
                    icon="fa-regular fa-bullhorn"
                    title="No press releases for this destination"
                    text="We couldn't find any press releases for the selected destination. Try clearing the filter to see all announcements."
                    action={
                      <button
                        type="button"
                        className="th-btn style1 th-icon"
                        onClick={() => handleDestinationChange("")}
                      >
                        Reset Filter
                      </button>
                    }
                  />
                ) : (
                  <EmptyState
                    icon="fa-regular fa-bullhorn"
                    title="No press releases yet"
                    text="There are no press releases available at this time. Please check back soon for the latest announcements."
                  />
                )}
              </div>
            )}
          </div>

          {/* Bottom pagination */}
          <div
            className="mc-pagination"
            style={{ display: totalPages > 1 ? "flex" : "none" }}
          >
            <button
              className="mc-pagination-btn mc-pagination-prev"
              disabled={currentPage <= 1 || loading}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <i className="fa fa-chevron-left"></i>
            </button>

            {paginationEntries.map((entry) =>
              entry.type === "ellipsis" ? (
                <span key={entry.key} className="mc-pagination-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={entry.key}
                  className={`mc-pagination-btn ${currentPage === entry.value ? "mc-pagination-active" : ""}`}
                  onClick={() => handlePageChange(entry.value)}
                  disabled={loading}
                >
                  {entry.value}
                </button>
              ),
            )}

            <button
              className="mc-pagination-btn mc-pagination-next"
              disabled={currentPage >= totalPages || loading}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <i className="fa fa-chevron-right"></i>
            </button>
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div className="col-xxl-4 col-lg-5">
          <aside className="sidebar-area position-sticky" style={{ top: 100 }}>
            {/* Destination filter */}
            {countries.length > 0 && (
              <div className="widget widget_categories destination-filter-widget">
                <h3 className="widget_title">Filter By Destination</h3>
                <ul className="destination-filter-list">
                  <li>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDestinationChange("");
                      }}
                      className={`pe-0 ${selectedDest === "" ? "fw-bold text-theme" : ""}`}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        width="25"
                        src="/main/img/theme-img/pin-mark.svg"
                        alt="Display all press releases"
                      />
                      All Destinations
                      {selectedDest === "" && (
                        <i className="fa fa-check-circle lh-base float-end"></i>
                      )}
                    </a>
                  </li>
                  {countries.map((country) => (
                    <li key={country.id}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDestinationChange(country.id);
                        }}
                        className={`pe-0 ${selectedDest === country.id ? "text-theme fw-bold" : ""}`}
                      >
                        <CountryFlag
                          country={country.name}
                          fallback={
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              width="25"
                              src="/main/img/theme-img/pin-mark.svg"
                              alt={country.name}
                              style={{ marginRight: 6, verticalAlign: "middle" }}
                            />
                          }
                        />
                        {country.name}
                        {selectedDest === country.id && (
                          <i className="fa fa-check-circle lh-base float-end"></i>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {sidebarContent}
          </aside>
        </div>
      </div>

      <style>{`
        /* Move the widget's padding onto its title and list so the destination
           list's scrollbar can sit flush with the card edge. Scoped under
           .sidebar-area to beat the theme's ".widget_categories ul" rules. */
        .sidebar-area .destination-filter-widget {
          padding: 0;
        }
        .sidebar-area .destination-filter-widget .widget_title {
          padding-top: var(--widget-padding-y, 30px);
          padding-left: var(--widget-padding-x, 30px);
          padding-right: var(--widget-padding-x, 30px);
        }
        .sidebar-area .destination-filter-widget .widget_title:after {
          left: var(--widget-padding-x, 30px);
        }
        .sidebar-area .destination-filter-list {
          padding: 0 var(--widget-padding-x, 30px) var(--widget-padding-y, 30px);
          margin-bottom: 0;
        }

        /* Scrollable destination list — constrained only on desktop (≥1024px).
           The widget title stays fixed; only the list below it scrolls. */
        @media (min-width: 1024px) {
          .sidebar-area .destination-filter-list {
            max-height: 420px;
            overflow-y: auto;
            scrollbar-width: thin;
            scrollbar-color: rgba(0, 0, 0, 0.18) transparent;
          }
          .sidebar-area .destination-filter-list::-webkit-scrollbar {
            width: 6px;
          }
          .sidebar-area .destination-filter-list::-webkit-scrollbar-track {
            background: transparent;
          }
          .sidebar-area .destination-filter-list::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.18);
            border-radius: 999px;
          }
          .sidebar-area .destination-filter-list:hover::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.32);
          }
        }
      `}</style>
    </div>
  );
}
